import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  fetchIntervalStats,
  fetchIntervals,
  postForecast,
  postEstimateTokens,
  fetchDowntimeMatrix
} from '../api/machineIntervalsApi.js';

export function useIntervalStats(filters = {}) {
  return useQuery({
    queryKey: ['machine-intervals', 'stats', filters],
    queryFn: () => fetchIntervalStats(filters),
    placeholderData: keepPreviousData
  });
}

export function useIntervals(filters = {}) {
  return useQuery({
    queryKey: ['machine-intervals', 'list', filters],
    queryFn: () => fetchIntervals(filters),
    placeholderData: keepPreviousData
  });
}

/**
 * The forecast is a POST because the planned run is the input, not a filter.
 * Disabled until there is a machine and at least one drum — asking for an
 * estimate of nothing just returns zeros and looks like a broken panel.
 */
/**
 * Per-drum estimate for a whole job. The result is WORK CONTENT — it ignores
 * queueing and phase dependencies, so callers must not label it a due date.
 */
export function useTokenEstimates(tokens = [], operator = null) {
  return useQuery({
    queryKey: ['machine-intervals', 'estimate-tokens', tokens, operator],
    queryFn: () => postEstimateTokens({ tokens, operator }),
    enabled: Array.isArray(tokens) && tokens.length > 0,
    placeholderData: keepPreviousData
  });
}

/**
 * Estimate for whole jobs. The backend expands each job into its unfinished
 * drums — a job runs its phases on different machines at different speeds, so it
 * cannot be costed as one lump — and returns a total per job.
 */
export function useJobEstimates(jobIds = [], operator = null) {
  const ids = Array.isArray(jobIds) ? [...new Set(jobIds.filter(Boolean))] : [];
  return useQuery({
    queryKey: ['machine-intervals', 'estimate-jobs', ids, operator],
    queryFn: () => postEstimateTokens({ jobIds: ids, operator }),
    enabled: ids.length > 0,
    placeholderData: keepPreviousData
  });
}

export function useForecast({ machineId, operator, tokens, previousToken } = {}) {
  return useQuery({
    queryKey: ['machine-intervals', 'forecast', machineId, operator, tokens, previousToken],
    queryFn: () => postForecast({ machineId, operator, tokens, previousToken }),
    enabled: !!machineId && Array.isArray(tokens) && tokens.length > 0,
    placeholderData: keepPreviousData
  });
}

export function useDowntimeMatrix(filters = {}) {
  return useQuery({
    queryKey: ['machine-intervals', 'downtime-matrix', filters],
    queryFn: () => fetchDowntimeMatrix(filters),
    placeholderData: keepPreviousData
  });
}
